import debugFactory from 'debug';

import {
  createEmitter,
  createIframe,
  createIpc,
  getContentWindow,
  listenToResize,
  setupFullscreenControl,
} from './bootstrap';
import { BootstrapConfig, encodeBootstrapConfig } from './bootstrap/bootstrap-config';
import { IpcInstance } from './bootstrap/ipc';
import { mapErrorMessageToIngridContext } from './bootstrap/map-error-message';
import {
  CheckoutIpc,
  Config,
  GenericEventModel,
  IPCEvents,
  MessagePayload,
  PublicAPI,
} from './bootstrap/types';

const debug = debugFactory('ingrid:bootstrap');

debug('Started bootstrap');

try {
  let preSetupWindow: Window<'pre_setup'>;
  let config: Config;

  try {
    preSetupWindow = window as unknown as Window<'pre_setup'>;
    config = preSetupWindow._sw.config;
  } catch (e) {
    throw new Error('config_error');
  }

  const relay = function () {
    /**
     * Synchronize iframe loading and ipc messaging. Queue messages until an
     * iframe declares its readiness.
     */
    const checkoutEmitter = createEmitter<any>();
    const fullscreenEmitter = createEmitter<MessagePayload>();

    checkoutIpc.listen(message => fullscreenEmitter.send(message));
    fullscreenIpc.listen(message => checkoutEmitter.send(message));

    checkoutIframe.addEventListener('load', () => checkoutEmitter.accept(checkoutIpc));
    fullscreenIframe.addEventListener('load', () => fullscreenEmitter.accept(fullscreenIpc));
  };

  const listenForOutsideIframeClicks = function (checkoutIpc: IpcInstance<IPCEvents>) {
    document.addEventListener('click', () => checkoutIpc.send({ name: 'outside_click' }));
  };

  const saveIngridUserId = function (ipc: CheckoutIpc) {
    ipc.listen(payload => {
      if (payload.name === 'user_id') {
        try {
          localStorage.setItem('delivery-preference-id', payload.data);
          debug('Saved ingrid delivery preference id');
        } catch (e) {
          console.warn('Local Storage is disabled');
        }
      }
    });
  };

  const removeSkeletonWhenReady = function (ipc: CheckoutIpc) {
    ipc.listen(payload => {
      if (payload.name === 'loaded') {
        const loader = document.getElementById('ingrid-loader');
        loader?.parentElement?.removeChild(loader);
        checkoutIframe.removeAttribute('hidden');
        config.container.style.removeProperty('min-height');
      }
    });
  };

  const listenForScrollChange = function (iframe: HTMLIFrameElement, ipc: CheckoutIpc) {
    ipc.listen(payload => {
      if (payload.name === 'scroll_widget') {
        const { top } = iframe.getBoundingClientRect();
        if (payload.data + top > 0) return;

        window.scrollTo({
          top: payload.data + top + window.scrollY,
          behavior: 'smooth',
        });
      }
    });
  };

  const setupCrossIframeCommunication = function () {
    const checkoutIpc = createIpc<IPCEvents>({
      window: getContentWindow(checkoutIframe),
      origin: new URL(config.bootstrapURL).origin,
    });

    const fullscreenIpc = createIpc<MessagePayload>({
      window: getContentWindow(fullscreenIframe),
      origin: new URL(config.bootstrapURL).origin,
    });

    return { checkoutIpc, fullscreenIpc };
  };

  const exposeAPI = function () {
    const api: PublicAPI = {
      suspend: () => {
        debug('Called suspend');

        checkoutIpc.send({
          name: 'suspend',
        });
      },

      resume: () => {
        debug('Called resume');

        checkoutIpc.send({
          name: 'resume',
        });
      },
      on: (name, callback) => {
        debug(`Called register callback: "${name}"`);

        checkoutIpc.listen(payload => {
          if (payload.name === name) {
            // data is 'void' on some events, so typescript won't let us access it unless we narrow the types down
            const genericEventData = (payload as GenericEventModel).data;

            // This is needed to support multiple arguments passed to the callback as we need to support it for legacy reasons
            if (Array.isArray(genericEventData)) {
              (callback as any)(...genericEventData);
              return;
            }
            callback(genericEventData);
          }
        });
      },
      destroy: callback => {
        debug('Checkout destroy started');
        checkoutIframeObserver.disconnect();
        fullscreenIframeObserver.disconnect();
        checkoutIpc.detach();
        fullscreenIpc.detach();
        checkoutIframe.parentElement?.removeChild(checkoutIframe);
        fullscreenIframe.parentElement?.removeChild(fullscreenIframe);
        (window as any)._sw = undefined;
        callback?.();
        debug('Destroy callback called');
      },
    };

    // Run all the callbacks that were added before this file got downloaded and executed
    const storedJSAPICallbacksArguments = preSetupWindow._sw.q || [];
    const callbackHandler = function (cb: (api: PublicAPI) => void) {
      cb(api);
    };
    // Callbacks are stored as 'Arguments', so can't be called directly.
    // Check the html snippet returned from SIW to soo how window._sw.q is created
    storedJSAPICallbacksArguments.forEach(args => callbackHandler(...args));

    window._sw = (callback: (publicAPI: PublicAPI) => void) => callback(api);
  };

  const createAndAppendIframes = function () {
    const { token, session, locale, bootstrapURL, features, customStylesheetPath } = config;
    const baseCheckoutHref = bootstrapURL.replace('bootstrap.js', 'checkout.html');
    const baseFullscreenHref = bootstrapURL.replace('bootstrap.js', 'fullscreen.html');

    try {
      if (localStorage.getItem('ingrid-id') !== null) {
        localStorage.removeItem('ingrid-id');
      }
    } catch (e) {
      console.warn('Local Storage is disabled');
    }

    let userId: string | undefined = undefined;
    if (
      (process.env.ENV === 'stage' ||
        process.env.ENV === 'development' ||
        process.env.ENV === 'local' ||
        process.env.ENV === 'production') &&
      !(features.external_address_book_provider || '').startsWith('HM')
    ) {
      try {
        userId = localStorage.getItem('delivery-preference-id') ?? undefined;

        // there were cases when the id wasn't properly saved in backend services, resulting an empty string
        if (userId === "''" || userId === '""' || userId === '') {
          localStorage.removeItem('delivery-preference-id');
          userId = undefined;
        }
      } catch (e) {
        console.warn('Local Storage is disabled');
      }
    }

    const bootstrapConfig: BootstrapConfig = {
      bootstrapURL,
      // Indicate who created the iframe to secure postMessage communication.
      creator: window.location.origin,
      locale,
      sessionId: session,
      token,
      userId,
      fullSentryTracing: features.enable_full_sentry_tracing,
      stateGetRetry: features.enable_state_get_retry,
      externalAddressBookProvider: features.external_address_book_provider,
      customStylesheetPath: customStylesheetPath,
    };

    /*
     * Security: We should pass all the sensitive data via the "hash" part of the
     * url, because this part is not send to the server but is avaiable for
     * browser only inside the iframe.
     */
    const checkoutIframe = createIframe(
      `${baseCheckoutHref}#${encodeBootstrapConfig(bootstrapConfig)}`
    );
    checkoutIframe.id = 'ingrid-widget-iframe';
    checkoutIframe.setAttribute('data-test', 'iframe-checkout');
    checkoutIframe.title = 'Ingrid checkout widget';

    const fullscreenIframe = createIframe(
      `${baseFullscreenHref}#${encodeBootstrapConfig(bootstrapConfig)}`
    );
    fullscreenIframe.id = 'ingrid-fullscreen-iframe';
    /**
     * NOTE: If you happen to use mapbox inside checkout.html,
     * please add a referrerPolicy attribute - Safari won't pass referrer to request
     * headers if not specified explicitly.
     *
     * Remember to add equivalent meta tag to template/checkout.html as well.
     */
    fullscreenIframe.referrerPolicy = 'strict-origin-when-cross-origin';
    fullscreenIframe.setAttribute('data-test', 'iframe-fullscreen');

    config.container.appendChild(checkoutIframe);
    document.body.appendChild(fullscreenIframe);

    return { checkoutIframe, fullscreenIframe };
  };

  const createIframeObserver = function (frame: HTMLIFrameElement) {
    const observer = new MutationObserver(() => {
      setTimeout(() => {
        if (!isNodeAttached(frame)) {
          (window as any)._sw = undefined;
          observer.disconnect();
          checkoutIpc.detach();
          fullscreenIpc.detach();
          checkoutIframe.parentElement?.removeChild(checkoutIframe);
          fullscreenIframe.parentElement?.removeChild(fullscreenIframe);
          debug(`Iframe cleaned up: ${frame.id}`);
        }
      }, 1);
    });

    return observer;
  };

  const setupCleanupOnIframeRemove = function (observer: MutationObserver) {
    observer.observe(document, {
      childList: true,
      subtree: true,
    });
  };

  const isNodeAttached = function (node: Node) {
    /*
     * This code allows to break out of a shadow DOM.
     */
    while (node.parentNode || node instanceof ShadowRoot) {
      if (node.parentNode) {
        node = node.parentNode;
      } else if (node instanceof ShadowRoot) {
        node = node.host;
      } else {
        break;
      }
    }

    return node instanceof HTMLDocument;
  };

  let checkoutIframe: HTMLIFrameElement;
  let fullscreenIframe: HTMLIFrameElement;

  try {
    ({ checkoutIframe, fullscreenIframe } = createAndAppendIframes());
  } catch (e) {
    throw new Error('iframe_creation_error');
  }

  let checkoutIpc: IpcInstance<IPCEvents>;
  let fullscreenIpc: IpcInstance<MessagePayload>;

  try {
    ({ checkoutIpc, fullscreenIpc } = setupCrossIframeCommunication());

    relay();
    listenToResize(checkoutIframe, checkoutIpc);
    listenForScrollChange(checkoutIframe, checkoutIpc);
    listenForOutsideIframeClicks(checkoutIpc);
    setupFullscreenControl(fullscreenIframe, checkoutIpc);
    removeSkeletonWhenReady(checkoutIpc);
  } catch (e) {
    throw new Error('iframe_internal_communication_establishment_error');
  }

  saveIngridUserId(checkoutIpc);

  try {
    exposeAPI();
  } catch (e) {
    throw new Error('frontend_api_error');
  }

  const checkoutIframeObserver = createIframeObserver(checkoutIframe);
  const fullscreenIframeObserver = createIframeObserver(fullscreenIframe);
  setupCleanupOnIframeRemove(checkoutIframeObserver);
  setupCleanupOnIframeRemove(fullscreenIframeObserver);
} catch (e) {
  if (e instanceof Error) {
    mapErrorMessageToIngridContext(e);
  } else {
    throw e;
  }
}
